import Head from "next/head";
import { useRouter } from "next/router";
import { TFunction, useTranslation } from "next-i18next";
import { ModifyURL } from "@/base/utils/fetch";
import { useTheme } from "@/base/components/theme/BaseThemeProvider";
import DesignConfig from "@/theme/design.config";

type Props = {
  title: string | ((t: TFunction) => string);
  description?: string | ((t: TFunction) => string);
};

const getValue = (
  value: string | undefined | null | ((t: TFunction) => string),
  t: TFunction
) => {
  if (!value) {
    return null;
  }

  if (typeof value === "string") {
    return value;
  }

  return value(t);
};

export default function MetaBlock({
  title: titleProp,
  description: descriptionProp,
}: Props) {
  const { t } = useTranslation();

  const siteUrl = process.env.NEXT_PUBLIC_APP_URL || "http://localhost";

  const { theme } = useTheme();
  const router = useRouter();
  const cleanPath = router.asPath.split("#")[0].split("?")[0];

  const canonicalUrl = `${siteUrl}${
    router.defaultLocale != router.locale ? `/${router.locale}` : ""
  }${router.asPath === "/" ? "" : cleanPath}`;

  const fullUrl = `${siteUrl}${router.asPath}`;
  const title = getValue(titleProp, t) || process.env.NEXT_PUBLIC_APP_NAME;

  const description =
    getValue(descriptionProp, t) ||
    t("base-common:meta.description") ||
    process.env.NEXT_PUBLIC_APP_NAME;

  let domain = siteUrl;
  // extract domain from full url

  try {
    const url = new URL(fullUrl);
    domain = url.hostname;
  } catch (e) {
    // console.error(e);
  }

  const ogImage = ModifyURL(`/pages/og-image`, {
    title: title,
    file: true,
    site: process.env.NEXT_PUBLIC_PAGE_SITE || undefined,
  });

  return (
    <Head>
      <link
        rel="apple-touch-icon"
        sizes="180x180"
        href="/image/apple-touch-icon.png"
      />
      <link
        rel="icon"
        type="image/png"
        sizes="32x32"
        href="/image/favicon-32x32.png"
      />
      <link
        rel="icon"
        type="image/png"
        sizes="16x16"
        href="/image/favicon-16x16.png"
      />
      <link rel="manifest" href="/image/site.webmanifest" />
      <link
        rel="mask-icon"
        href="/image/safari-pinned-tab.svg"
        color="#5bbad5"
      />
      <link rel="shortcut icon" href="/favicon.ico" />
      <meta
        name="apple-mobile-web-app-title"
        content={process.env.NEXT_PUBLIC_APP_NAME}
      />
      <meta
        name="application-name"
        content={process.env.NEXT_PUBLIC_APP_NAME}
      />
      <meta name="msapplication-TileColor" content="#da532c" />
      <meta name="msapplication-config" content="/image/browserconfig.xml" />
      <meta
        name="theme-color"
        content={DesignConfig[theme || "dark"].browserThemeColor}
      />

      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="author" content="bohdanchik.com" />

      <meta property="og:url" content={fullUrl} />
      <meta property="og:type" content="website" />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={ogImage} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta property="twitter:domain" content={domain} />
      <meta property="twitter:url" content={fullUrl} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />
      <meta
        name="viewport"
        content="width=device-width, initial-scale=1, viewport-fit=cover"
      />

      <meta property="og:locale" content={router.locale} />

      <link rel="canonical" href={canonicalUrl} />
    </Head>
  );
}
